import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  VaRadio,
  VaRadioOption,
} from '@department-of-veterans-affairs/component-library/dist/react-bindings';

import SubTaskContainerHeader from '../SubTaskContainerHeader';
import { usePathHistory } from '../utils/tracking/PathHistoryContext';
import {
  trackScreenerStepView,
  trackClaimTypeSelection,
  trackScreenerValidationError,
  trackExternalLinkClick,
} from '../../utils/tracking/features/screenerTracking';
import pageNames from './pageNames';

const dataKey = 'view:claimType';
const militaryStatusKey = 'view:militaryStatus';

// Active-duty members separating in fewer than 90 days arrive here straight
// from release-date (too late for BDD, but still a standard claim);
// everyone else comes from military-status.
const getBackPage = data =>
  data[militaryStatusKey] === 'active-duty'
    ? pageNames.releaseDate
    : pageNames.militaryStatus;

const content = {
  label: 'Which of these options apply to your condition?',
  errorMessage: 'Select an option that applies to your condition',
};

const options = [
  {
    value: 'new',
    label: 'I have 1 or more conditions that I haven’t claimed before',
    description:
      'You can claim any illness or injury (known as a condition) related to your military service. Select this option for conditions that may qualify under the PACT Act.',
  },
  {
    value: 'increase',
    label: 'One or more of my conditions has gotten worse',
    description:
      'You’ll file a claim for increase on a previously rated disability.',
  },
  {
    value: 'both',
    label: 'Both of these apply to me',
    description:
      'You’ll file a claim that includes both an increase on a previously rated disability and a new condition you haven’t claimed before.',
  },
];

const optionValues = options.map(option => option.value);

const validate = data => optionValues.includes(data[dataKey]);

/**
 * Start 2 — Type of claim screener
 * @param {Object} data - subtask data
 * @param {Boolean} error - page submitted & error state
 * @param {Function} setPageData - updates subtask data
 * @returns {JSX}
 */
const ClaimTypePage = ({ data = {}, error, setPageData }) => {
  const { addStep, getHistory } = usePathHistory();

  useEffect(() => {
    trackScreenerStepView({
      stepName: pageNames.claimType,
      pathHistoryContext: { addStep, getHistory },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (error) {
      trackScreenerValidationError({
        pageName: pageNames.claimType,
        fieldName: 'claim-type',
        errorMessage: content.errorMessage,
      });
    }
  }, [error]);

  const handleSelect = ({ target }) => {
    setPageData({ [dataKey]: target.value || null });
  };

  return (
    <>
      <SubTaskContainerHeader />
      <VaRadio
        label={content.label}
        labelHeaderLevel="2"
        error={error ? content.errorMessage : null}
        onRadioOptionSelected={handleSelect}
        className="vads-u-margin-top--0"
        required
      >
        {options.map(({ value, label, description }) => (
          <VaRadioOption
            key={value}
            className="vads-u-margin-y--2"
            name="claimType"
            id={value}
            value={value}
            label={label}
            description={description}
            checked={value === data[dataKey]}
            tile
          />
        ))}
      </VaRadio>
      <va-details
        label="What to do if you disagree with a VA decision on a previously submitted claim"
        class="vads-u-display--block vads-u-margin-top--3"
      >
        <p className="vads-u-margin-top--0">
          If you disagree with our decision on a claim you already submitted,
          you’ll need to request a decision review. This includes conditions
          that may now qualify under the PACT Act.
        </p>
        <va-link
          external
          href="/decision-reviews/"
          text="Learn more about the decision review process"
          onClick={() =>
            trackExternalLinkClick({
              linkLabel: 'Learn more about the decision review process',
              pageName: pageNames.claimType,
              pathHistoryData: getHistory(),
            })
          }
        />
      </va-details>
    </>
  );
};

ClaimTypePage.propTypes = {
  data: PropTypes.shape({}),
  error: PropTypes.bool,
  setPageData: PropTypes.func,
};

export default {
  name: pageNames.claimType,
  component: ClaimTypePage,
  validate,
  back: getBackPage,
  next: pageNames.pactEligibility,
  onContinue: data => trackClaimTypeSelection(data[dataKey]),
};
